import { type GraphQLTaggedNode } from 'react-relay';

import { readFragmentInterop } from '../useReadFragmentInterop.ts';

import { recordOptimisticFragmentWriteRollback } from './optimisticFragmentWriteRollback.ts';

type WriteFragment<TData, TVariables extends Record<string, unknown>> = (options: {
	fragmentId: string;
	data: TData;
	fragment: GraphQLTaggedNode;
	variables?: TVariables;
	useAggClient?: boolean;
	__callerPackage__?: string;
}) => void;

// Shared setup for fragment writes captured during Relay optimistic mutation
// updates. The write function is injected by the interop module to keep this
// helper free of a runtime import cycle with writeFragmentInterop. Replaying
// through the original write path also preserves normal fragment bridge sync
// when the cache bridge experiments are enabled.
export function recordOptimisticFragmentWrite<
	TData,
	TVariables extends Record<string, unknown> = Record<string, unknown>,
>({
	fragment,
	fragmentId,
	data,
	variables,
	useAggClient,
	callerPackage,
	writeFragment,
}: {
	fragment: GraphQLTaggedNode;
	fragmentId: string;
	data: TData;
	variables?: TVariables;
	useAggClient?: boolean;
	callerPackage?: string;
	writeFragment: WriteFragment<TData, TVariables>;
}) {
	recordOptimisticFragmentWriteRollback<TData, TVariables>({
		fragment,
		fragmentId,
		variables,
		callerPackage,
		readPreviousData: () =>
			readFragmentInterop<TData, TVariables>({
				id: fragmentId,
				fragment,
				variables,
				interopApolloAggClient: useAggClient,
				__callerPackage__: callerPackage,
			}),
		writePreviousData: (previousData) =>
			writeFragment({
				fragmentId,
				fragment,
				data: previousData,
				variables,
				useAggClient,
				__callerPackage__: callerPackage,
			}),
		writeData: () =>
			writeFragment({
				fragmentId,
				fragment,
				data,
				variables,
				useAggClient,
				__callerPackage__: callerPackage,
			}),
	});
}
